import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { RefreshCw, AlertCircle, Receipt, Search } from 'lucide-react';
import { getCustomerWithCamaras } from '../api/AdminDashboard.api';
import { getMonthlyBillingTypes } from '../../camaraManager/api/camaraManager.api';
import { DAY_BUCKETS, getCamerasByBucket, buildMrrRows, filterRowsByCustomer } from '../utils/cobranzas';

const MRR_SECTION = 'mrr';
const BASE_PATH = '/cobranzas';

const SECTIONS = [
  ...DAY_BUCKETS.map((b) => ({ key: b.key, label: b.label })),
  { key: MRR_SECTION, label: 'MRR – renovaciones' },
];
const DEFAULT_SECTION = SECTIONS[0].key;

const formatDate = (date) =>
  date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const countLabel = (n) => `${n} ${n === 1 ? 'cámara' : 'cámaras'}`;

const TH = 'text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap';
const TD = 'py-3 px-4 text-slate-700 whitespace-nowrap';

// Single "Cobranzas" page: tabs live in the URL (/cobranzas/:section) and data is fetched once for all of them.
const Cobranzas = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const isMrr = section === MRR_SECTION;
  const isKnown = SECTIONS.some((s) => s.key === section);

  const [customers, setCustomers] = useState([]);
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [billingError, setBillingError] = useState(false);
  // Each tab keeps its own customer search.
  const [queries, setQueries] = useState({});

  const load = async () => {
    setLoading(true);
    setError(false);
    setBillingError(false);
    const [customersResult, billingResult] = await Promise.allSettled([
      getCustomerWithCamaras(),
      getMonthlyBillingTypes(),
    ]);
    if (customersResult.status === 'fulfilled') {
      setCustomers(customersResult.value);
    } else {
      setCustomers([]);
      setError(true);
    }
    if (billingResult.status === 'fulfilled') {
      setBilling(billingResult.value);
    } else {
      setBilling([]);
      setBillingError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const rowsBySection = useMemo(() => {
    const result = {};
    DAY_BUCKETS.forEach((b) => {
      result[b.key] = getCamerasByBucket(customers, b.key);
    });
    result[MRR_SECTION] = buildMrrRows(customers, billing);
    return result;
  }, [customers, billing]);

  if (!section) return <Navigate to={`${BASE_PATH}/${DEFAULT_SECTION}`} replace />;
  if (!isKnown) {
    return <p className="text-slate-500 font-medium">Sección no encontrada</p>;
  }

  const query = queries[section] || '';
  const allRows = rowsBySection[section];
  const rows = filterRowsByCustomer(allRows, query);
  const setQuery = (value) => setQueries((prev) => ({ ...prev, [section]: value }));
  const failed = error || (isMrr && billingError);
  const showCounts = !loading && !error;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Cobranzas</h2>
          {!loading && !failed && <p className="text-sm text-slate-500">{countLabel(rows.length)}</p>}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 min-h-[44px] lg:min-h-0 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      <div role="tablist" className="flex gap-2 overflow-x-auto border-b border-slate-200">
        {SECTIONS.map((s) => {
          const selected = s.key === section;
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={selected}
              onClick={() => navigate(`${BASE_PATH}/${s.key}`)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                selected ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {s.label}
              {showCounts && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${selected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                  {rowsBySection[s.key].length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar cliente"
          aria-label="Buscar cliente"
          className="w-full min-h-[44px] lg:min-h-0 pl-10 pr-4 py-2 rounded-xl text-sm text-slate-700 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Cargando...</p>
        </div>
      )}

      {!loading && failed && (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">No se pudieron obtener los datos</p>
        </div>
      )}

      {!loading && !failed && rows.length === 0 && (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Receipt className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">{allRows.length === 0 ? 'No hay cámaras en esta categoría' : 'Ningún cliente coincide con la búsqueda'}</p>
        </div>
      )}

      {!loading && !failed && rows.length > 0 && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className={TH}>Cliente</th>
                  <th className={TH}>Cámara</th>
                  {isMrr ? (
                    <>
                      <th className={TH}>Día de renovación</th>
                      <th className={TH}>Próxima renovación</th>
                      <th className={TH}>Días para renovar</th>
                    </>
                  ) : (
                    <th className={TH}>Días restantes</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.identifier} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className={TD}>{row.customerName}</td>
                    <td className={TD}>{row.cameraName}</td>
                    {isMrr ? (
                      <>
                        <td className={TD}>{row.renewalDay}</td>
                        <td className={TD}>{formatDate(row.nextRenewal)}</td>
                        <td className={TD}>{row.daysUntil}</td>
                      </>
                    ) : (
                      <td className={TD}>{row.daysLeft}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cobranzas;
